/**
 * 1stbuddha — policy.js
 *
 * <policy-page> (snippets/policy-layout.liquid)
 *   - "On this page": gives each H2 of the document an id and lists them
 *     (shown from 1280px when there are at least two); the link of the
 *     section being read gets aria-current
 *   - "Other policies" select (below 1024px): options from the nav list,
 *     changing it opens that policy
 */
(() => {
  if (customElements.get('policy-page')) return;

  const slug = (text) =>
    text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section';

  class PolicyPage extends HTMLElement {
    connectedCallback() {
      this.buildSelect();
      this.buildToc();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    buildSelect() {
      const field = this.querySelector('[data-policy-select-field]');
      const select = this.querySelector('[data-policy-select]');
      const items = [...this.querySelectorAll('[data-policy-nav] > li')];
      if (!field || !select || items.length < 2) return;

      items.forEach((item) => {
        const option = document.createElement('option');
        option.value = item.dataset.url;
        option.textContent = item.dataset.title;
        option.selected = item.hasAttribute('data-current');
        select.append(option);
      });
      if (!items.some((item) => item.hasAttribute('data-current'))) {
        const option = document.createElement('option');
        option.value = '';
        option.textContent = '—';
        option.selected = true;
        option.disabled = true;
        select.prepend(option);
      }
      select.addEventListener('change', () => {
        if (select.value) window.location.assign(select.value);
      });
      field.hidden = false;
    }

    buildToc() {
      const toc = this.querySelector('[data-policy-toc]');
      const list = this.querySelector('[data-policy-toc-list]');
      const headings = [...this.querySelectorAll('[data-policy-main] h2')].filter((heading) => heading.textContent.trim());
      if (!toc || !list || headings.length < 2) return;

      const used = new Set();
      const links = headings.map((heading) => {
        if (!heading.id) {
          let id = slug(heading.textContent.trim());
          while (used.has(id) || document.getElementById(id)) id = `${id}-${used.size + 1}`;
          heading.id = id;
        }
        used.add(heading.id);
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        item.append(link);
        list.append(item);
        return link;
      });
      toc.hidden = false;

      if (!('IntersectionObserver' in window)) return;
      const setCurrent = (id) =>
        links.forEach((link) => {
          if (link.getAttribute('href') === `#${id}`) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
      this.observer = new IntersectionObserver(
        (entries) => {
          const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible[0]) setCurrent(visible[0].target.id);
        },
        { rootMargin: '-20% 0px -65% 0px' }
      );
      headings.forEach((heading) => this.observer.observe(heading));
      setCurrent(headings[0].id);
    }
  }

  customElements.define('policy-page', PolicyPage);
})();
